import { useCallback } from 'react';
import type {
  UseFormGetValues,
  UseFormSetValue,
  UseFieldArrayAppend,
  UseFieldArrayMove,
  UseFieldArrayRemove,
} from 'react-hook-form';
import type { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';
import type { Question } from '@aphp-ema/common/questionnaire';
import type { QuestionOption } from '@aphp-ema/common/questionnaire';
import { checkReorderConditions } from '@/features/questionnaires/utils/checkReorderConditions';
import {
  checkQuestionDeletionConditions,
  checkAllowMultipleChangeConditions,
} from '@/features/questionnaires/utils/checkQuestionChangeConditions';
import { useQuestionChangeWarning } from '@/features/questionnaires/hooks/questionnaire/useQuestionChangeWarning';
import { useQuestionWarningDialog } from './useQuestionWarningDialog';

type UseQuestionOperationsProps = {
  questions: Question[] | undefined;
  getValues: UseFormGetValues<QuestionnaireFormData>;
  setValue: UseFormSetValue<QuestionnaireFormData>;
  move: UseFieldArrayMove;
  remove: UseFieldArrayRemove;
  append: UseFieldArrayAppend<QuestionnaireFormData, 'questions'>;
};

export const useQuestionOperations = ({
  questions,
  getValues,
  setValue,
  move,
  remove,
  append,
}: UseQuestionOperationsProps) => {
  const warningDialog = useQuestionWarningDialog({
    getValues,
    setValue,
    move,
    remove,
  });

  const questionChangeWarning = useQuestionChangeWarning({
    questions: questions || [],
  });

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!questions) return;

      const brokenConditions = checkReorderConditions(
        questions,
        fromIndex,
        toIndex,
      );

      if (brokenConditions.length > 0) {
        warningDialog.setPendingReorder(fromIndex, toIndex);
        warningDialog.showWarning(
          'reorder',
          brokenConditions,
          warningDialog.handleReorderConfirm,
        );
      } else {
        move(fromIndex, toIndex);
      }
    },
    [questions, move, warningDialog],
  );

  const handleDelete = useCallback(
    (index: number) => {
      if (!questions) return;

      const questionToDelete = questions[index];
      if (!questionToDelete) return;

      const brokenConditions = checkQuestionDeletionConditions(
        questions.filter((_, i) => i !== index),
        questionToDelete.id,
      );

      if (brokenConditions.length > 0) {
        warningDialog.setPendingDeletion(index);
        warningDialog.showWarning(
          'deletion',
          brokenConditions,
          warningDialog.handleDeletionConfirm,
        );
      } else {
        remove(index);
      }
    },
    [questions, remove, warningDialog],
  );

  const handleTypeChange = useCallback(
    (questionId: string, onChange: () => void) => {
      const brokenConditions =
        questionChangeWarning.checkTypeChange(questionId);
      if (brokenConditions) {
        warningDialog.setPendingChange(onChange);
        warningDialog.showWarning(
          'typeChange',
          brokenConditions,
          warningDialog.handleChangeConfirm,
        );
      } else {
        onChange();
      }
    },
    [questionChangeWarning, warningDialog],
  );

  const handleOptionsChange = useCallback(
    (
      questionId: string,
      oldOptions: QuestionOption[] | null,
      newOptions: QuestionOption[] | null,
      onChange: () => void,
    ) => {
      const brokenConditions = questionChangeWarning.checkOptionsChange(
        questionId,
        oldOptions,
        newOptions,
      );
      if (brokenConditions) {
        warningDialog.setPendingChange(onChange);
        warningDialog.showWarning(
          'optionChange',
          brokenConditions,
          warningDialog.handleChangeConfirm,
        );
      } else {
        onChange();
      }
    },
    [questionChangeWarning, warningDialog],
  );

  const handleAllowMultipleChange = useCallback(
    (questionId: string, newAllowMultiple: boolean, onChange: () => void) => {
      if (!questions) return;

      const brokenConditions = checkAllowMultipleChangeConditions(
        questions,
        questionId,
        newAllowMultiple,
      );

      if (brokenConditions.length > 0) {
        warningDialog.setPendingChange(onChange);
        warningDialog.showWarning(
          'typeChange',
          brokenConditions,
          warningDialog.handleChangeConfirm,
        );
      } else {
        onChange();
      }
    },
    [questions, warningDialog],
  );

  const addNewQuestion = useCallback(() => {
    append({
      id: crypto.randomUUID(),
      order: 0,
      label: '',
      internalName: '',
      internalDescription: '',
      required: false,
      description: '',
      parameters: {
        type: 'text',
        size: 'S' as const,
      },
      displayConditions: null,
    });
  }, [append]);

  return {
    handleReorder,
    handleDelete,
    handleTypeChange,
    handleOptionsChange,
    handleAllowMultipleChange,
    addNewQuestion,
    warningState: warningDialog.warningState,
  };
};
